import { OptionsHttpService } from "@/api/http/OptionsHttpService";
import type { CheckoutOptions, Options } from "@/types/OptionsTypes";
import { createGlobalState, useLocalStorage } from "@vueuse/core";
import { useReadonlyWithSetter } from "@/composables/common";
import { computed } from "vue";
import type { CheckoutDataField } from "@/api/http/schemas/checkoutSchemas";

const useOptionsState = createGlobalState(() => {
  const [options, setOptions] = useReadonlyWithSetter(
    useLocalStorage<Options | null>("options", null, {
      serializer: {
        read: (v: string | null) => (v ? JSON.parse(v) : null),
        write: (v: Options | null) => JSON.stringify(v ?? {}),
      },
    })
  );

  const [checkoutOptions, setCheckoutOptions] = useReadonlyWithSetter<CheckoutOptions | null>(null);

  const documentTypeDropdownOptions = computed(() => {
    const fileTypeOptions = options.value?.file_types;

    if (!fileTypeOptions) {
      return [];
    }

    return Object.values(fileTypeOptions)
      .flatMap((value) => value.categories)
      .map((category) => ({ id: category.type, label: category.name }));
  });

  const getOptions = async () => {
    const data = await new OptionsHttpService().getOptions();
    setOptions(data);
  };

  const getCheckoutOptions = async () => {
    const data = await new OptionsHttpService().getCheckoutOptions();
    setCheckoutOptions(data);
  };

  const getDefinitionForField = (field: CheckoutDataField, blockName: string) => {
    const blocks = checkoutOptions.value?.blocks;

    if (!blocks || !blockName) {
      return null;
    }

    const block = blocks.find((b) => b.id === blockName);
    return block?.fields.find((fieldDefinition) => fieldDefinition.id === field.name) ?? null;
  };

  return {
    checkoutOptions,
    documentTypeDropdownOptions,
    options,
    getCheckoutOptions,
    setCheckoutOptions,
    getOptions,
    setOptions,
    getDefinitionForField,
  };
});

export default useOptionsState;
